import { useState, useMemo } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import LoadingButton from '@mui/lab/LoadingButton';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { useAlert } from '@lendica/components';
import { Company } from '@lendica/utils';
import { useOfferTermsApi } from '../api';
import { useCompanyContext, useUser, useRefreshUser } from '@lendica/hooks';
import { updateUserEmail, requestInitialEmailVerification } from '@lendica/auth';

interface CompanyInfoDialogProps {
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
    company: Company;
}

interface FormValues {
    companyName: string;
    email: string;
    userEmail: string;
    useSameEmail: boolean;
}

export const CompanyInfoDialog = ({ open, onClose, onSaved, company }: CompanyInfoDialogProps) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { prefetchCompanyDetails, membershipsLoading } = useCompanyContext();
    const user = useUser();
    const refreshUser = useRefreshUser();
    const [loading, setLoading] = useState(false);

    const api = useOfferTermsApi();
    const { setSuccessMessage, setErrorMessage, setNeutralMessage } = useAlert();

    const initialValues: FormValues = useMemo(
        () => ({
            companyName: company?.company_name ?? '',
            email: company?.email ?? '',
            userEmail: user?.email ?? '',
            useSameEmail: false,
        }),
        [company?.company_name, company?.email, user?.email]
    );

    const validationSchema = useMemo(
        () =>
            Yup.object({
                companyName: Yup.string().trim().required('Company name is required'),
                email: Yup.string()
                    .trim()
                    .email('Enter a valid email address')
                    .required('Contact person email is required'),
                userEmail: Yup.string()
                    .trim()
                    .when('useSameEmail', {
                        is: false,
                        then: schema =>
                            schema
                                .email('Enter a valid email address')
                                .required('Your work email is required'),
                        otherwise: schema => schema.notRequired(),
                    }),
                useSameEmail: Yup.boolean(),
            }),
        []
    );

    const handleSave = async (values: FormValues) => {
        setLoading(true);

        const trimmedCompanyName = values.companyName.trim();
        const trimmedEmail = values.email.trim();
        const trimmedUserEmail = values.userEmail.trim();
        const shouldUpdateUserEmail =
            (trimmedUserEmail && trimmedUserEmail !== user?.email) || values.useSameEmail;

        const handleApiError = ({
            error,
            prefix,
            defaultMessage,
        }: {
            error: any;
            prefix?: string;
            defaultMessage?: string;
        }) => {
            setErrorMessage(
                prefix +
                    (error?.response?.data?.message ||
                        error?.response?.data?.error ||
                        error?.message ||
                        defaultMessage),
                { timeout: 6000 }
            );
            console.error(error);
        };

        try {
            try {
                await api.updateCompany(company.id, {
                    company_name: trimmedCompanyName,
                    email: trimmedEmail,
                });
                setSuccessMessage('Company information updated.');
            } catch (error: any) {
                handleApiError({ error, defaultMessage: 'Failed to update company information. ' });
                return;
            }

            if (shouldUpdateUserEmail) {
                const targetEmail = values.useSameEmail ? trimmedEmail : trimmedUserEmail;
                try {
                    await updateUserEmail(targetEmail);
                    await requestInitialEmailVerification();
                    setNeutralMessage(
                        'We sent you a verification email. Please click the link to verify your email before inviting customers.',
                        { timeout: 6000 }
                    );
                    await refreshUser({ force: true });
                } catch (error: any) {
                    handleApiError({ error, prefix: 'Failed to update your account email. ' });
                    return;
                }
            }

            try {
                await prefetchCompanyDetails([company.id], { force: true });
            } catch (error) {
                console.error(error);
            }

            onSaved();
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            fullScreen={fullScreen}
            open={open}
            onClose={onClose}
            maxWidth="sm"
            PaperProps={{
                sx: {
                    paddingY: 1,
                    paddingX: 1,
                },
            }}
        >
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={handleSave}
                validateOnMount
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    isValid,
                    isSubmitting,
                }) => {
                    const canSubmit = isValid && !loading && !isSubmitting;

                    return (
                        <Form>
                            <DialogTitle>Company Information</DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    Please complete your company information below before inviting
                                    customers.
                                </DialogContentText>
                                <Stack sx={{ mt: 1, py: 2 }}>
                                    <TextField
                                        name="companyName"
                                        label="Company name"
                                        fullWidth
                                        value={values.companyName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.companyName && !!errors.companyName}
                                        helperText={
                                            (touched.companyName && errors.companyName) || ' '
                                        }
                                        required
                                    />
                                    <TextField
                                        name="email"
                                        label="Contact person email"
                                        type="email"
                                        fullWidth
                                        value={values.email}
                                        onChange={e => {
                                            handleChange(e);
                                            if (values.useSameEmail) {
                                                setFieldValue('userEmail', e.target.value);
                                            }
                                        }}
                                        onBlur={handleBlur}
                                        error={touched.email && !!errors.email}
                                        helperText={(touched.email && errors.email) || ' '}
                                        required
                                    />
                                </Stack>
                                {!membershipsLoading && !user?.email && (
                                    <>
                                        <Divider />
                                        <Stack sx={{ mt: 1, py: 2, gap: 2 }}>
                                            <Typography variant="h6">
                                                Finish setting up your account
                                            </Typography>
                                            <Typography variant="body1" color="text.secondary">
                                                Add your work email below for login and receiving
                                                account notifications.
                                            </Typography>
                                            <Stack sx={{ gap: 1 }}>
                                                <FormControlLabel
                                                    label="Use the same email as above."
                                                    control={
                                                        <Checkbox
                                                            name="useSameEmail"
                                                            checked={values.useSameEmail}
                                                            onChange={e => {
                                                                const checked = e.target.checked;
                                                                setFieldValue(
                                                                    'useSameEmail',
                                                                    checked
                                                                );
                                                                if (checked) {
                                                                    setFieldValue(
                                                                        'userEmail',
                                                                        values.email
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    }
                                                />
                                                {!values.useSameEmail && (
                                                    <TextField
                                                        name="userEmail"
                                                        label="Your work email"
                                                        type="email"
                                                        fullWidth
                                                        value={values.userEmail}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={
                                                            touched.userEmail && !!errors.userEmail
                                                        }
                                                        helperText={
                                                            (touched.userEmail &&
                                                                errors.userEmail) ||
                                                            "You'll need to verify this email before inviting customers."
                                                        }
                                                        required={!values.useSameEmail}
                                                    />
                                                )}
                                            </Stack>
                                        </Stack>
                                    </>
                                )}
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={onClose}>Close</Button>
                                <LoadingButton
                                    type="submit"
                                    variant="contained"
                                    loading={loading}
                                    disabled={!canSubmit}
                                >
                                    Save
                                </LoadingButton>
                            </DialogActions>
                        </Form>
                    );
                }}
            </Formik>
        </Dialog>
    );
};
